import { Action, Dispatch } from "redux";
import { ThunkAction, ThunkDispatch } from "redux-thunk";
import { loadAndTrack } from "redux-request-loading";

import { Payload } from "@neworbit/redux-helpers";

import { AnsweredQuestionWithId } from "@teamprevent/common/questionnaires/model";
import { questionnaireLoaded, SUBMIT_QUESTIONNAIRE, saveQuestionnaireSuccess } from "@teamprevent/common/questionnaires/actions";
import { QUESTIONNAIRE_KEY } from "@teamprevent/common/referralReport/components/ReferralReportV4";

import { TasksUpdatedSuccessResult, CreateTaskDto } from "../task/model";
import { consultationLoaded } from "../consultations/actions";
import { OhCaseDetailModel } from "../crud/ohCase/model";
import { ApplicationState } from "../clinicalHistory/components/ClinicalHistory";

import * as actions from "./actionTypes";
import { ReferralReportApi } from "./ReferralReportApi";

export const SAVE_REFERRAL_REPORT = "SAVE_REFERRAL_REPORT";

export type ReferralReportAction =
    ReferralReportSubmitSuccess |
    SendToEmployeeForConsentReferralReportSuccess |
    ApproveReferralReportSuccess |
    RequestChangesReferralReportSuccess |
    RejectReferralReportSuccess |
    RequestReferralReportEditSuccess;
export type ReferralReportSubmitSuccess = ({ type: typeof actions.SUBMIT_REFERRAL_REPORT_SUCCESS }) & Payload<OhCaseDetailModel>;
export type SendToEmployeeForConsentReferralReportSuccess = ({ type: typeof actions.SENDTOEMPLOYEE_REFERRAL_REPORT_SUCCESS }) & Payload<OhCaseDetailModel>;
export type ApproveReferralReportSuccess = ({ type: typeof actions.APPROVE_REFERRAL_REPORT_SUCCESS }) & Payload<OhCaseDetailModel>;
export type RequestChangesReferralReportSuccess = ({ type: typeof actions.REQUEST_CHANGES_REFERRAL_REPORT_SUCCESS }) & Payload<TasksUpdatedSuccessResult>;
export type RejectReferralReportSuccess = ({ type: typeof actions.REJECT_REFERRAL_REPORT_SUCCESS }) & Payload<TasksUpdatedSuccessResult>;
export type RequestReferralReportEditSuccess = ({ type: typeof actions.REQUEST_REFERRAL_REPORT_EDIT_SUCCESS }) & Payload<TasksUpdatedSuccessResult>;

export const loadQuestionnaire = (ohCaseId: string, taskId: string, questionnaireKey: string, omittedSections?: string[]) => {
    return async (dispatch: Dispatch) => {
        const response = await loadAndTrack(dispatch, "loadQuestionnaire", ReferralReportApi.load(ohCaseId, taskId));
        if (response) {
            let questions = response.data?.questions ?? [];
            if (omittedSections && omittedSections.length > 0) {
                questions = questions.filter(q => !omittedSections.some(s => s === q.sectionId));
            }
            dispatch(questionnaireLoaded({
                questions,
                questionnaireKey,
                eTag: response.eTag,
                isDefaultCustomerType: response.isDefaultCustomerType,
                customerType: response.customerType
            }));
        }
        return response;
    };
};

export const loadQuestionnairePreview = (ohCaseId: string, taskId: string, questionnaireKey: string, omittedSections?: string[]) => {
    return async (dispatch: Dispatch) => {
        const response = await loadAndTrack(dispatch, "loadQuestionnaire", ReferralReportApi.loadPreview(ohCaseId, taskId));
        if (response) {
            let questions = response.data?.questions ?? [];
            if (omittedSections && omittedSections.length > 0) {
                questions = questions.filter(q => !omittedSections.some(s => s === q.sectionId));
            }
            dispatch(questionnaireLoaded({
                questions,
                questionnaireKey,
                eTag: response.eTag,
                isDefaultCustomerType: response.isDefaultCustomerType,
                customerType: response.customerType
            }));
        }
        return response;
    };
};

export const loadConsultationDetails = ({ ohCaseId, taskId }: { ohCaseId: string; taskId: string }) => {
    return async (dispatch: Dispatch) => {
        const consultationDetails = await loadAndTrack(dispatch, "loadConsultation", ReferralReportApi.loadConsultation(ohCaseId, taskId));
        dispatch(consultationLoaded(consultationDetails));
    };
};

export const loadConsultationDetailsPreview = ({ ohCaseId, taskId }: { ohCaseId: string; taskId: string }) => {
    return async (dispatch: Dispatch) => {
        const consultationDetails = await loadAndTrack(dispatch, "loadConsultation", ReferralReportApi.loadConsultationPreview(ohCaseId, taskId));
        dispatch(consultationLoaded(consultationDetails));
    };
};

export const saveReferralReport = (ohCaseId: string, taskId: string, questions: AnsweredQuestionWithId[], eTag: string) => {
    return async (dispatch: Dispatch) => {
        const response = await loadAndTrack(dispatch, SAVE_REFERRAL_REPORT, ReferralReportApi.save(ohCaseId, taskId, questions, eTag));
        dispatch(saveQuestionnaireSuccess({ questionnaireKey: QUESTIONNAIRE_KEY, eTag: response.eTag }));
    };
};

export const submitReferralReport = (ohCaseId: string, taskId: string, createTasks: CreateTaskDto[]) => {
    return async (dispatch: Dispatch) => {
        const result = await loadAndTrack(dispatch, SUBMIT_QUESTIONNAIRE, ReferralReportApi.submit(ohCaseId, taskId, createTasks));
        dispatch(submitReferralReportSuccess(result));
    };
};

export const submitReferralReportAmendment = (ohCaseId: string, taskId: string) => {
    return async (dispatch: Dispatch) => {
        const result = await loadAndTrack(dispatch, SUBMIT_QUESTIONNAIRE, ReferralReportApi.submitAmendment(ohCaseId, taskId));
        dispatch(submitReferralReportSuccess(result));
    };
};

export const submitReferralReportEdit = (ohCaseId: string, taskId: string): ThunkAction<Promise<any>, ApplicationState, undefined, Action> => {
    return async (dispatch: ThunkDispatch<ApplicationState, undefined, Action>) => {
        const result = await loadAndTrack(dispatch, SUBMIT_QUESTIONNAIRE, ReferralReportApi.submitEdit(ohCaseId, taskId));
        dispatch(submitReferralReportSuccess(result));
    };
};

export const sendToEmployeeForConsentReferralReport = (ohCaseId: string, taskId: string, notes: string) => {
    return async (dispatch: Dispatch) => {
        const result = await loadAndTrack(
            dispatch,
            actions.SENDTOEMPLOYEE_REFERRAL_REPORT, ReferralReportApi.sendToEmployeeForConsent(ohCaseId, taskId, notes));
        dispatch(sendToEmployeeForConsentReferralReportSuccess(result));
    };
};

export const approveReferralReport = (ohCaseId: string, taskId: string, notes: string) => {
    return async (dispatch: Dispatch) => {
        const result = await loadAndTrack(dispatch, actions.APPROVE_REFERRAL_REPORT, ReferralReportApi.approve(ohCaseId, taskId, notes));
        dispatch(approveReferralReportSuccess(result));
    };
};

export const rejectReferralReport = (ohCaseId: string, taskId: string, notes: string) => {
    return async (dispatch: Dispatch) => {
        const results = await loadAndTrack(dispatch, actions.REJECT_REFERRAL_REPORT, ReferralReportApi.reject(ohCaseId, taskId, notes));
        dispatch(rejectReferralReportSuccess({ ohCaseId, tasks: results }));
    };
};

export const requestChangesReferralReport = (ohCaseId: string, taskId: string, notes: string) => {
    return async (dispatch: Dispatch) => {
        const results =
            await loadAndTrack(dispatch, actions.REQUEST_CHANGES_REFERRAL_REPORT, ReferralReportApi.requestChanges(ohCaseId, taskId, notes));
        dispatch(requestChangesReferralReportSuccess({ ohCaseId, tasks: results }));
    };
};

export const requestReferralReportEdit = (ohCaseId: string, taskId: string, reasonForEdit: string) => {
    return async (dispatch: Dispatch) => {
        const results = await loadAndTrack(
            dispatch, actions.REQUEST_REFERRAL_REPORT_EDIT, ReferralReportApi.requestReportEdit(ohCaseId, taskId, reasonForEdit));
        dispatch(requestReferralReportEditSuccess({ ohCaseId, tasks: results }));
    };
};

export const submitReferralReportSuccess = (payload: OhCaseDetailModel): ReferralReportSubmitSuccess => {
    return {
        type: actions.SUBMIT_REFERRAL_REPORT_SUCCESS,
        payload
    };
};

export const sendToEmployeeForConsentReferralReportSuccess = (payload: OhCaseDetailModel): SendToEmployeeForConsentReferralReportSuccess => {
    return {
        type: actions.SENDTOEMPLOYEE_REFERRAL_REPORT_SUCCESS,
        payload
    };
};

export const approveReferralReportSuccess = (payload: OhCaseDetailModel): ApproveReferralReportSuccess => {
    return {
        type: actions.APPROVE_REFERRAL_REPORT_SUCCESS,
        payload
    };
};

export const requestChangesReferralReportSuccess = (payload: TasksUpdatedSuccessResult): RequestChangesReferralReportSuccess => {
    return {
        type: actions.REQUEST_CHANGES_REFERRAL_REPORT_SUCCESS,
        payload
    };
};

export const rejectReferralReportSuccess = (payload: TasksUpdatedSuccessResult): RejectReferralReportSuccess => {
    return {
        type: actions.REJECT_REFERRAL_REPORT_SUCCESS,
        payload
    };
};

export const requestReferralReportEditSuccess = (payload: TasksUpdatedSuccessResult): RequestReferralReportEditSuccess => {
    return {
        type: actions.REQUEST_REFERRAL_REPORT_EDIT_SUCCESS,
        payload
    };
};
